import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { LocalNotifications } from '@capacitor/local-notifications'
import { hasReachedDestination, useRiderStore } from '../store/rider'
import { useTransitStore } from '../store/transit'
import { storeSelect, shallow } from '../lib/store.svelte'
import {
  updateArrivalDetection,
  vehicleProximity,
  type RiderLocation,
} from '../lib/rideArrival'
import { api } from '../services/api'

let notifId = 1

async function sendNotification(title: string, body: string) {
  try {
    await LocalNotifications.schedule({
      notifications: [{ id: notifId++, title, body }],
    })
  } catch {
    // silently ignore if notifications unavailable
  }
}

export function useRideTracking(getLocation: () => RiderLocation | null = () => null) {
  // Each effect below reads a NARROW selector, not whole rider state. Load-bearing, not
  // tidiness: these effects write rider state, so one reading the whole store would re-run
  // on its own write — restarting the 30 s poll every refresh, or spinning outright.
  const fetchDeps = storeSelect(
    useRiderStore,
    (s) => ({
      isRiding: s.isRiding,
      trackedVehicleId: s.trackedVehicleId,
      hasVehicle: s.trackedVehicle !== null,
      isBus: s.trackedVehicle?.type === 'bus',
      tripId: s.trackedVehicle?.tripId,
    }),
    shallow,
  )
  const trackedId = storeSelect(useRiderStore, (s) => s.trackedVehicleId)
  const milestone = storeSelect(
    useRiderStore,
    (s) => ({
      isRiding: s.isRiding,
      destinationStopId: s.destinationStopId,
      stopsUntilDestination: s.stopsUntilDestination,
    }),
    shallow,
  )
  const vehicles = storeSelect(useTransitStore, (s) => s.vehicles)
  const arrival = storeSelect(
    useRiderStore,
    (s) => ({
      isRiding: s.isRiding,
      trackedVehicleId: s.trackedVehicleId,
      trackedVehicle: s.trackedVehicle,
      destinationStopId: s.destinationStopId,
      reached: hasReachedDestination(s),
    }),
    shallow,
  )

  // Request notification permissions once.
  $effect(() => {
    LocalNotifications.requestPermissions().catch(() => {})
  })

  // Stop predictions on ride start, refreshed every 30 s. Visited stops are prepended back
  // on refresh, since the feed returns only remaining ones.
  //
  // Subways fetch by tripId (GTFS-RT trip updates); buses have no usable trip stop-times
  // cached, so they fetch onward calls live by vehicle id. Both return the same { updates }
  // shape, so the mapping below is identical.
  $effect(() => {
    const { isRiding, hasVehicle, isBus, tripId, trackedVehicleId } = fetchDeps.value

    if (!isRiding || !hasVehicle) return
    if (isBus ? !trackedVehicleId : !tripId) return

    let live = true

    const fetchStops = () => {
      const req = isBus
        ? api.getBusVehicleStops(trackedVehicleId!)
        : api.getTripUpdates(tripId!)
      req
        .then((data) => {
          if (!live) return
          const fresh = data.updates.map((u) => ({
            stopId: u.stopId,
            stopName: u.stopName ?? u.stopId,
            stopSequence: u.stopSequence,
            arrivalTime: u.arrivalTime,
            departureTime: u.departureTime,
            arrivalDelay: u.arrivalDelay ?? null,
          }))

          // Preserve already-visited stops so they stay in the list
          const { upcomingStops, currentStopIndex, setUpcomingStops } = useRiderStore.getState()
          // currentStopIndex is the last departed/current stop, so it belongs in the
          // preserved prefix too. This is especially important at the selected stop: an
          // onward-only refresh may already omit it, but the arrival state must survive
          // long enough to distinguish staying aboard from stepping off.
          const visited = upcomingStops.slice(0, currentStopIndex + 1)
          const freshIds = new Set(fresh.map((s) => s.stopId))
          const uniqueVisited = visited.filter((s) => !freshIds.has(s.stopId))
          setUpcomingStops([...uniqueVisited, ...fresh])
        })
        .catch(() => {})
    }

    fetchStops()
    const interval = setInterval(fetchStops, 30_000)

    return () => {
      live = false
      clearInterval(interval)
    }
  })

  // Keep tracked vehicle in sync with live store data.
  $effect(() => {
    const id = trackedId.value
    if (!id) return
    const live = vehicles.value[id]
    if (live) useRiderStore.getState().setTrackedVehicle(live)
  })

  // Haptics + notifications at destination milestones. On a planned trip the last stop of a
  // leg is a transfer, not an arrival, so the 0-stop rung hands off to the next leg rather
  // than ending the ride. `prevStopsUntil` lives outside the effect so it survives re-runs
  // and each milestone fires once per TRANSITION, not per invocation.
  let prevStopsUntil = useRiderStore.getState().stopsUntilDestination
  $effect(() => {
    const { isRiding, destinationStopId, stopsUntilDestination } = milestone.value
    const prev = prevStopsUntil
    prevStopsUntil = stopsUntilDestination

    // No destination means nothing to count down to: stopsUntilDestination is pinned at 0
    // there and must not read as "you have arrived".
    if (!isRiding || !destinationStopId || stopsUntilDestination === prev) return

    if (stopsUntilDestination === 2) {
      Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {})
      void sendNotification('Getting close!', '2 stops until your destination')
    } else if (stopsUntilDestination === 1) {
      Haptics.impact({ style: ImpactStyle.Heavy }).catch(() => {})
      void sendNotification('Next stop is yours', 'Prepare to exit the vehicle')
    } else if (stopsUntilDestination === 0) {
      const { nextLeg, transferWalk, upcomingStops, currentStopIndex } = useRiderStore.getState()
      const next = nextLeg()
      Haptics.notification().catch(() => {})

      if (next) {
        const walk = transferWalk()
        const here = upcomingStops[currentStopIndex]?.stopName ?? 'your transfer'
        void sendNotification(
          `Get off at ${here}`,
          walk
            ? `${walk.minutes} min walk to ${walk.toName}, then take the ${next.routeId}`
            : `Transfer to the ${next.routeId}`,
        )
      } else {
        void sendNotification('Get off now!', "You've reached your destination")
      }
    }
  })

  // Reaching the selected stop changes the sign and sends the alert, but it does not by
  // itself mean the rider stepped off. First observe the phone and vehicle together; only
  // a later, clearly separated fix completes the leg. Missing/noisy location stays onboard.
  let arrivalKey: string | null = null
  let confirmedTogether = false
  $effect(() => {
    const state = arrival.value
    const key = state.isRiding && state.reached
      ? `${state.trackedVehicleId ?? ''}|${state.destinationStopId ?? ''}`
      : null

    if (!key) {
      arrivalKey = null
      confirmedTogether = false
      return
    }
    if (key !== arrivalKey) {
      arrivalKey = key
      confirmedTogether = false
    }

    const detection = updateArrivalDetection(
      confirmedTogether,
      vehicleProximity(getLocation(), state.trackedVehicle),
    )
    confirmedTogether = detection.confirmedTogether
    if (!detection.shouldExit) return

    // leaveVehicle advances a planned leg at its destination and ends an unplanned ride.
    arrivalKey = null
    useRiderStore.getState().leaveVehicle()
  })
}
